'use client';

import { useState, memo, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { useStore } from '@/lib/store';
import { tmdb } from '@/lib/tmdb';
import { parseTMDBDate } from '@/lib/utils';
import Image from 'next/image';
import Link from 'next/link';
import { useLongPress } from 'use-long-press';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MobileContextMenu } from '@/components/MobileContextMenu';
import {
  Plus,
  Check,
  Star,
  Calendar,
  Clock,
  PlayCircle,
  X,
  CheckCircle2,
  Tv as TvIcon,
  Circle,
  ChevronDown,
  AlertCircle,
  Eye,
  EyeOff,
  Lock
} from 'lucide-react';
import { format } from 'date-fns';

interface MediaItem {
  id: number;
  title?: string;
  name?: string;
  poster_path: string | null;
  backdrop_path: string | null;
  vote_average: number;
  overview?: string;
  release_date?: string;
  first_air_date?: string;
  media_type?: 'tv' | 'movie';
  genre_ids?: number[];
}

interface MediaCardProps {
  item: MediaItem;
  mediaType?: 'tv' | 'movie';
  showAddButton?: boolean;
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

interface SeasonDetail {
  season_number: number;
  episode_count: number;
  poster_path: string | null;
  name: string;
  air_date: string | null;
  episodes?: Array<{
    air_date: string | null;
    episode_number: number;
    name: string;
  }>;
}

const MediaCardComponent = ({
  item,
  mediaType,
  showAddButton = true,
  size = 'medium',
  className = ''
}: MediaCardProps) => {
  const router = useRouter();
  const { user } = useAuth();
  const { addToWatchlist, removeFromWatchlist, watchlist, dislikedItems, addToDisliked, removeFromDisliked, isDisliked: checkIsDisliked, toggleMovieWatched, markSeasonWatched, markSeasonUnwatched, markEpisodeWatched, markEpisodeUnwatched } = useStore();
  const [isAdding, setIsAdding] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showSeasonDialog, setShowSeasonDialog] = useState(false);
  const [seasons, setSeasons] = useState<SeasonDetail[]>([]);
  const [loadingSeasons, setLoadingSeasons] = useState(false);
  const [expandedSeasons, setExpandedSeasons] = useState<Set<number>>(new Set());
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const seasonScrollRef = useRef<HTMLDivElement>(null);

  const title = item.title || item.name;
  const date = item.release_date || item.first_air_date;
  const year = date ? new Date(date).getFullYear() : null;
  const type = mediaType || item.media_type || (item.first_air_date ? 'tv' : 'movie');

  // Check if item is already in watchlist
  const isInWatchlist = watchlist.some(w => w.id === item.id);
  const isDisliked = checkIsDisliked(item.id);
  const watchlistItem = watchlist.find(w => w.id === item.id);
  const isWatched = watchlistItem?.watched || false;

  const getSizeClasses = () => {
    switch (size) {
      case 'small':
        return 'w-32 sm:w-40';
      case 'large':
        return 'w-48 sm:w-56 md:w-64';
      default:
        return 'w-40 sm:w-48 md:w-52';
    }
  };

  const handleAddToWatchlist = async (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!user) {
      router.push('/auth/signin');
      return;
    }

    // Save scroll position of the parent scroll container
    const target = e?.currentTarget as HTMLElement | null;
    const scrollContainer = target?.closest('.overflow-x-auto') as HTMLElement | null;
    const scrollPosition = scrollContainer?.scrollLeft || 0;

    // If already in watchlist, remove it
    if (isInWatchlist) {
      try {
        await removeFromWatchlist(item.id);
          // Restore scroll position after state update with multiple attempts
        if (scrollContainer) {
          const restore = () => { scrollContainer.scrollLeft = scrollPosition; };
          requestAnimationFrame(() => {
            restore();
            requestAnimationFrame(() => {
              restore();
              setTimeout(restore, 0);
              setTimeout(restore, 10);
              setTimeout(restore, 50);
            });
          });
        }
      } catch (error) {
        // Failed to remove from watchlist
      }
      return;
    }

    setIsAdding(true);

    try {
      if (type === 'tv') {
        // Import ContentChecker dynamically for TV shows
        const { ContentChecker } = await import('@/lib/contentChecker');

        // Fetch full show details and calculate update schedule
        const updateDetails = await ContentChecker.checkSingleShow(item.id, 0);

        const watchlistItem = {
          id: item.id,
          type: 'tv' as const,
          name: title || '',
          posterPath: item.poster_path,
          backdropPath: item.backdrop_path,
          overview: item.overview || '',
          watchedSeasons: [],
          lastChecked: new Date().toISOString(),
          hasNewContent: updateDetails.hasNewSeason || updateDetails.hasNewEpisodes,
          totalSeasons: updateDetails.totalSeasons || 0,
          addedAt: new Date().toISOString(),
          rating: item.vote_average,
          genres: item.genre_ids || [],
          // Intelligent update scheduling
          status: updateDetails.status,
          nextCheckDate: updateDetails.nextCheckDate,
          updateFrequency: updateDetails.updateFrequency,
          // Content details populated immediately
          newSeasonNumber: updateDetails.hasNewSeason ? updateDetails.newSeasonNumber : undefined,
          nextEpisode: updateDetails.nextEpisode,
        };

        await addToWatchlist(watchlistItem);
      } else {
        // Movies - use simple logic
        const watchlistItem = {
          id: item.id,
          type: 'movie' as const,
          name: title || '',
          posterPath: item.poster_path,
          backdropPath: item.backdrop_path,
          overview: item.overview || '',
          lastChecked: new Date().toISOString(),
          hasNewContent: false,
          addedAt: new Date().toISOString(),
          rating: item.vote_average,
          genres: item.genre_ids || []
        };

        await addToWatchlist(watchlistItem);
      }

      // Restore scroll position after adding to watchlist with multiple attempts
      if (scrollContainer) {
        const restore = () => { scrollContainer.scrollLeft = scrollPosition; };
        requestAnimationFrame(() => {
          restore();
          requestAnimationFrame(() => {
            restore();
            setTimeout(restore, 0);
            setTimeout(restore, 10);
            setTimeout(restore, 50);
          });
        });
      }
    } catch (error) {
      // Failed to add to watchlist
    } finally {
      setIsAdding(false);
    }
  };

  const handleDislike = () => {
    if (!user) return;

    if (isDisliked) {
      removeFromDisliked(item.id);
    } else {
      // Remove from watchlist if it's there
      if (isInWatchlist) {
        removeFromWatchlist(item.id);
      }
      addToDisliked({
        id: item.id,
        type: type,
        name: title || '',
        dislikedAt: new Date().toISOString(),
        genres: item.genre_ids || [],
      });
    }
  };

  const handleMarkWatched = () => {
    if (!user || type !== 'movie') return;
    toggleMovieWatched(item.id);
  };

  const handleOpenSeasonDialog = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowSeasonDialog(true);
  };

  // Load season details when dialog opens
  useEffect(() => {
    if (showSeasonDialog && type === 'tv' && seasons.length === 0) {
      loadSeasonDetails();
    }
  }, [showSeasonDialog]);

  // Prevent body scroll when dialog is open
  useEffect(() => {
    if (showSeasonDialog) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showSeasonDialog]);

  const loadSeasonDetails = async () => {
    setLoadingSeasons(true);
    try {
      const showDetails = await tmdb.getShowWithDetails(item.id);
      if (showDetails.seasons) {
        const regularSeasons = showDetails.seasons.filter((s: any) => s.season_number > 0);

        const seasonFetchPromises = regularSeasons.map((season: any) => {
          return (async function(seasonData: any) {
            const seasonNumber = seasonData.season_number;
            try {
              const seasonDetail = await tmdb.getSeason(item.id, seasonNumber);
              return {
                id: seasonDetail.id || seasonData.id,
                season_number: seasonNumber,
                name: seasonDetail.name || seasonData.name,
                poster_path: seasonDetail.poster_path || seasonData.poster_path,
                air_date: seasonDetail.air_date || seasonData.air_date,
                episode_count: seasonDetail.episode_count || seasonData.episode_count,
                episodes: seasonDetail.episodes || []
              };
            } catch (error) {
              return {
                id: seasonData.id,
                season_number: seasonNumber,
                name: seasonData.name,
                poster_path: seasonData.poster_path,
                air_date: seasonData.air_date,
                episode_count: seasonData.episode_count,
                episodes: []
              };
            }
          })(season);
        });

        const seasonsWithEpisodes = await Promise.all(seasonFetchPromises);
        setSeasons(seasonsWithEpisodes);
      }
    } catch (error) {
      // Failed to load season details
    } finally {
      setLoadingSeasons(false);
    }
  };

  // Drag handlers for pull-to-close
  const handleDragStart = (e: React.TouchEvent) => {
    const scrollContainer = seasonScrollRef.current;
    if (scrollContainer && scrollContainer.scrollTop === 0) {
      setIsDragging(true);
      setDragStartY(e.touches[0].clientY);
    }
  };

  const handleDragMove = (e: React.TouchEvent) => {
    if (!isDragging) return;

    const touch = e.touches[0];
    const currentY = touch.clientY;
    const offset = Math.max(0, currentY - dragStartY);

    // Prevent default scroll behavior when pulling down
    if (offset > 0) {
      e.preventDefault();
    }

    setDragOffset(offset);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    // Close dialog if dragged down more than 150px
    if (dragOffset > 150) {
      setShowSeasonDialog(false);
    }
    // Reset offset
    setDragOffset(0);
    setDragStartY(0);
  };

  const handleSeasonToggle = (seasonNumber: number, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const watchlistItem = watchlist.find(w => w.id === item.id);
    if (!watchlistItem || watchlistItem.type !== 'tv') return;

    const isWatched = watchlistItem.watchedSeasons.includes(seasonNumber);
    if (isWatched) {
      markSeasonUnwatched(item.id, seasonNumber);
    } else {
      markSeasonWatched(item.id, seasonNumber);
    }
  };

  // Long press handler for mobile (touch devices only)
  const longPressHandler = useLongPress(
    () => {
      // Only show menu on touch devices
      if (user && ('ontouchstart' in window || navigator.maxTouchPoints > 0)) {
        setShowMobileMenu(true);
      }
    },
    {
      threshold: 800, // 0.8 seconds hold time
      captureEvent: true,
      cancelOnMovement: 15, // Very low tolerance - cancel if finger moves more than 15px
      detect: 'touch', // Only detect touch events, not mouse
      onStart: (event) => {
        // Prevent browser's native context menu
        event.preventDefault();
      },
    }
  );

  return (
    <>
      <div
        className={`${getSizeClasses()} flex-shrink-0 group ${className}`}
        {...longPressHandler()}
        onContextMenu={(e) => {
          // Prevent browser context menu on touch devices
          if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
            e.preventDefault();
          }
        }}
        style={{ WebkitTouchCallout: 'none', WebkitUserSelect: 'none' }}
      >
        <div className="relative z-0">
          <Link href={`/${type}/${item.id}`} className="block">
            <div className="relative aspect-[2/3] overflow-hidden rounded-xl bg-transparent transition-all duration-500 group-hover:scale-105">
              {item.poster_path && (
                <Image
                  src={tmdb.getImageUrl(item.poster_path, 'w500')}
                  alt={title || 'Media poster'}
                  fill
                  sizes={`(max-width: 640px) ${size === 'small' ? '128px' : size === 'large' ? '192px' : '160px'}, (max-width: 768px) ${size === 'small' ? '160px' : size === 'large' ? '224px' : '192px'}, ${size === 'small' ? '160px' : size === 'large' ? '256px' : '208px'}`}
                  className="object-cover transition-transform duration-700 group-hover:scale-110"
                  loading="lazy"
                />
              )}

              {/* Full height gradient overlay - only show on hover for non-watchlist items */}
              <div className={`absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent transition-opacity duration-300 ${isInWatchlist ? 'opacity-0 group-hover:opacity-100' : 'opacity-0 group-hover:opacity-100'}`} />

              {/* Hover overlay covering full container */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col justify-between p-4 pointer-events-none">
            {/* Top section with rating and type */}
            <div className="flex items-start justify-between">
              {item.vote_average > 0 && (
                <div className="flex items-center gap-1 bg-black/80 rounded-full px-2 py-1">
                  <Star className="h-3 w-3 fill-yellow-500 text-yellow-500" />
                  <span className="text-xs text-white font-medium">
                    {item.vote_average.toFixed(1)}
                  </span>
                </div>
              )}
              <Badge className="bg-black/80 text-white text-xs border-0">
                {type === 'tv' ? 'TV SHOW' : 'MOVIE'}
              </Badge>
            </div>

            {/* Center play button */}
            <div className="flex items-center justify-center">
              <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center shadow-xl transform scale-0 group-hover:scale-100 transition-transform duration-300">
                <PlayCircle className="h-8 w-8 text-white" />
              </div>
            </div>

            {/* Bottom section with year only - button is now outside this overlay */}
            <div className="flex items-end justify-between">
              {year && (
                <div className="flex items-center gap-1 text-gray-300">
                  <Calendar className="h-3 w-3" />
                  <span className="text-xs">{year}</span>
                </div>
              )}
            </div>
          </div>

          {/* Buttons container - Bottom bar on mobile when in watchlist, traditional button on desktop */}
          {showAddButton && user && (
            <>
              {/* Mobile: Glassmorphic buttons when in watchlist */}
              {isInWatchlist && ('ontouchstart' in window || navigator.maxTouchPoints > 0) && (
                <div className="absolute bottom-0 left-0 right-0 z-20 pointer-events-auto md:hidden">
                  <div className="mx-3 mb-2 flex gap-2 items-center">
                    {/* Secondary Button - Movie watched or TV episodes (stretched width, same height as checkmark) */}
                    {type === 'movie' ? (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleMarkWatched();
                        }}
                        className={`flex-1 flex items-center justify-center gap-2 rounded-full backdrop-blur-md transition-all duration-300 shadow-xl active:scale-[0.98] ${
                          isWatched
                            ? 'bg-black/60 border-2 border-blue-500/80 active:bg-black/70'
                            : 'bg-black/60 border-2 border-gray-500/80 active:bg-black/70'
                        }`}
                        style={{ height: '40px' }}
                        title={isWatched ? 'Mark as Unwatched' : 'Mark as Watched'}
                      >
                        {isWatched ? (
                          <>
                            <Eye className="h-4 w-4 text-blue-400" />
                            <span className="text-[11px] font-bold text-white">Watched</span>
                          </>
                        ) : (
                          <>
                            <EyeOff className="h-4 w-4 text-gray-400" />
                            <span className="text-[11px] font-bold text-white">Unwatched</span>
                          </>
                        )}
                      </button>
                    ) : type === 'tv' && watchlistItem ? (
                      <button
                        onClick={handleOpenSeasonDialog}
                        className="flex-1 flex items-center justify-center gap-1.5 rounded-full backdrop-blur-md transition-all duration-300 shadow-xl bg-black/60 border-2 border-blue-500/80 active:bg-black/90 active:scale-[0.98]"
                        style={{ height: '40px' }}
                      >
                        <TvIcon className="h-4 w-4 text-blue-400" />
                        <span className="text-xs font-bold text-white">
                          {watchlistItem.watchedSeasons?.length || 0}/{watchlistItem.totalSeasons || '?'}
                        </span>
                      </button>
                    ) : null}

                    {/* In Watchlist - Toggle button (Check/Plus) - Stays circular, same size as plus */}
                    <button
                      onClick={handleAddToWatchlist}
                      disabled={isAdding}
                      className="flex items-center justify-center rounded-full backdrop-blur-md transition-all duration-300 shadow-xl active:scale-95 bg-black/60 border-2 border-green-500/80 active:bg-black/70"
                      style={{ width: '40px', height: '40px', minWidth: '40px', minHeight: '40px' }}
                    >
                      {isAdding ? (
                        <Clock className="h-5 w-5 animate-spin text-white" />
                      ) : (
                        <Check className="h-5 w-5 text-green-500" />
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Desktop OR Mobile when NOT in watchlist: Circular glassmorphic button */}
              <div className={`absolute bottom-3 right-3 z-10 transition-opacity duration-300 pointer-events-auto ${
                isInWatchlist
                  ? 'hidden md:block md:opacity-100'
                  : 'opacity-100 md:opacity-0 md:group-hover:opacity-100'
              }`}>
                <button
                  onClick={handleAddToWatchlist}
                  disabled={isAdding}
                  className={`flex items-center justify-center rounded-full backdrop-blur-md transition-all duration-300 shadow-xl disabled:opacity-50 disabled:cursor-not-allowed hover:scale-110 ${
                    isInWatchlist
                      ? 'bg-black/60 border-2 border-green-500/80 hover:bg-black/70'
                      : 'bg-black/60 border-2 border-red-500/80 hover:bg-black/70'
                  } text-white`}
                  style={{ width: '40px', height: '40px', minWidth: '40px', minHeight: '40px' }}
                >
                  {isAdding ? (
                    <Clock className="h-5 w-5 animate-spin" />
                  ) : isInWatchlist ? (
                    <Check className="h-5 w-5 text-green-500" />
                  ) : (
                    <Plus className="h-5 w-5 text-red-500" />
                  )}
                </button>
              </div>
            </>
          )}
        </div>
          </Link>

          {/* Title and description */}
          <Link href={`/${type}/${item.id}`} className="block mt-3">
            <div className="space-y-2">
              <p className="text-base font-semibold text-white truncate group-hover:text-red-400 transition-colors duration-300">
                {title}
              </p>
              {item.overview && size !== 'small' && (
                <p className="text-sm text-gray-400 leading-tight line-clamp-2">
                  {item.overview}
                </p>
              )}
            </div>
          </Link>
        </div>
      </div>

      {/* Mobile Context Menu */}
      <MobileContextMenu
        isOpen={showMobileMenu}
        onClose={() => setShowMobileMenu(false)}
        title={title || 'Unknown'}
        isInWatchlist={isInWatchlist}
        isDisliked={isDisliked}
        isWatched={isWatched}
        mediaType={type}
        onAddToWatchlist={handleAddToWatchlist}
        onRemoveFromWatchlist={() => removeFromWatchlist(item.id)}
        onDislike={handleDislike}
        onMarkWatched={type === 'movie' ? handleMarkWatched : undefined}
        onPlay={() => router.push(`/${type}/${item.id}`)}
      />

      {/* Season Dialog - Mobile Only */}
      {showSeasonDialog && type === 'tv' && watchlistItem && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/70 z-50 animate-in fade-in duration-200"
            style={{ touchAction: 'none' }}
            onClick={() => setShowSeasonDialog(false)}
          />

          {/* Dialog */}
          <div
            className="fixed inset-x-0 bottom-0 z-50 animate-in slide-in-from-bottom duration-300"
            style={{
              transform: `translateY(${dragOffset}px)`,
              transition: isDragging ? 'none' : 'transform 0.3s ease-out'
            }}
          >
            <div className="bg-gradient-to-b from-gray-900 to-black rounded-t-3xl shadow-2xl max-h-[85vh] overflow-hidden">
              {/* Header */}
              <div className="px-6 py-4 flex items-center justify-between border-b border-gray-800">
                <div>
                  <h3 className="text-xl font-bold text-white truncate">{title}</h3>
                  <span className="text-sm text-gray-400">
                    {watchlistItem.watchedSeasons?.length || 0}/{watchlistItem.totalSeasons || 0} seasons watched
                  </span>
                </div>
                <button
                  onClick={() => setShowSeasonDialog(false)}
                  className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-800 transition-colors"
                >
                  <X className="h-5 w-5 text-gray-400" />
                </button>
              </div>

              {/* Season List */}
              <div
                ref={seasonScrollRef}
                className="overflow-y-auto max-h-[calc(85vh-100px)] px-4 py-6"
                onTouchStart={handleDragStart}
                onTouchMove={handleDragMove}
                onTouchEnd={handleDragEnd}
              >
                {loadingSeasons ? (
                  <div className="text-center py-12">
                    <div className="inline-block h-10 w-10 animate-spin rounded-full border-4 border-red-600 border-t-transparent"></div>
                    <p className="text-gray-400 mt-4">Loading seasons...</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {seasons.map((season) => {
                      const isWatched = watchlistItem.watchedSeasons?.includes(season.season_number) || false;
                      const watchedEpisodeCount = watchlistItem.watchedEpisodes?.[season.season_number]?.length || 0;
                      const totalEpisodes = season.episodes?.length || season.episode_count || 0;
                      const isExpanded = expandedSeasons.has(season.season_number);

                      // Check if season has any future episodes
                      const now = new Date();
                      const hasFutureEpisodes = season.episodes?.some((ep: any) => {
                        const epAirDate = ep.air_date ? parseTMDBDate(ep.air_date)! : null;
                        return epAirDate && epAirDate > now;
                      });

                      const seasonDisabled = hasFutureEpisodes && !isWatched;

                      return (
                        <div key={season.season_number} className="space-y-2">
                          {/* Season Header */}
                          <div className={`rounded-2xl overflow-hidden ${
                            isWatched
                              ? 'bg-gradient-to-br from-green-950/40 to-green-900/20 ring-2 ring-green-600/30'
                              : 'bg-gradient-to-br from-gray-800/50 to-gray-900/50 ring-1 ring-gray-700/50'
                          }`}>
                            <button
                              onClick={(e) => !seasonDisabled && handleSeasonToggle(season.season_number, e)}
                              disabled={seasonDisabled}
                              className="w-full p-4 active:scale-[0.98] transition-transform disabled:cursor-not-allowed"
                            >
                              <div className="flex items-center gap-3">
                                {/* Season Poster */}
                                <div className="relative w-14 h-20 rounded-lg overflow-hidden bg-gray-900 flex-shrink-0 shadow-lg">
                                  {season.poster_path ? (
                                    <Image
                                      src={tmdb.getImageUrl(season.poster_path, 'w200')}
                                      alt={season.name}
                                      width={56}
                                      height={80}
                                      className="object-cover w-full h-full"
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                      <TvIcon className="h-5 w-5 text-gray-600" />
                                    </div>
                                  )}
                                </div>

                                {/* Season Details */}
                                <div className="flex-1 min-w-0 text-left">
                                  <h4 className="text-white font-bold text-base mb-1.5 line-clamp-1">
                                    {season.name}
                                  </h4>

                                  {/* Progress Bar */}
                                  <div className="mb-2">
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="text-xs font-medium text-gray-300">
                                        {watchedEpisodeCount}/{totalEpisodes} episodes
                                      </span>
                                      {season.air_date && (
                                        <span className="text-xs text-gray-500">
                                          {format(parseTMDBDate(season.air_date)!, 'yyyy')}
                                        </span>
                                      )}
                                    </div>
                                    <div className="h-1.5 bg-gray-800/70 rounded-full overflow-hidden">
                                      <div
                                        className={`h-full transition-all duration-500 ${
                                          isWatched ? 'bg-green-500' : 'bg-red-500'
                                        }`}
                                        style={{ width: `${totalEpisodes > 0 ? (watchedEpisodeCount / totalEpisodes) * 100 : 0}%` }}
                                      />
                                    </div>
                                  </div>
                                </div>

                                {/* Status Icon */}
                                <div className="flex-shrink-0">
                                  {isWatched ? (
                                    <div className="w-10 h-10 rounded-full bg-green-600/20 border-2 border-green-500 flex items-center justify-center">
                                      <CheckCircle2 className="h-5 w-5 text-green-400" />
                                    </div>
                                  ) : seasonDisabled ? (
                                    <div className="w-10 h-10 rounded-full bg-gray-700/50 border-2 border-gray-600 flex items-center justify-center">
                                      <Lock className="h-4 w-4 text-gray-500" />
                                    </div>
                                  ) : (
                                    <div className="w-10 h-10 rounded-full border-2 border-gray-600 flex items-center justify-center">
                                      <Circle className="h-4 w-4 text-gray-500" />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </button>

                            {/* Expand/Collapse button */}
                            {season.episodes && season.episodes.length > 0 && (
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();

                                  const newExpanded = new Set(expandedSeasons);
                                  if (isExpanded) {
                                    newExpanded.delete(season.season_number);
                                  } else {
                                    newExpanded.add(season.season_number);
                                  }
                                  setExpandedSeasons(newExpanded);
                                }}
                                className="w-full py-2 px-4 flex items-center justify-center gap-2 border-t border-gray-700/50 active:bg-gray-800/50 transition-colors"
                              >
                                <span className="text-xs font-medium text-gray-400">
                                  {isExpanded ? 'Hide' : 'Show'} Episodes
                                </span>
                                <ChevronDown className={`h-3.5 w-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                              </button>
                            )}
                          </div>

                          {/* Episode List */}
                          {isExpanded && season.episodes && (
                            <div className="pl-2 space-y-2">
                              {season.episodes.map((episode: any) => {
                                const isEpisodeWatched = watchlistItem.watchedEpisodes?.[season.season_number]?.includes(episode.episode_number) || false;

                                // Check if episode is in the future
                                const now = new Date();
                                const episodeAirDate = episode.air_date ? parseTMDBDate(episode.air_date)! : null;
                                const isInFuture = episodeAirDate && episodeAirDate > now;

                                // Check if any previous episode in this season has a future air date
                                const hasFutureEpisodeBefore = season.episodes.some((ep: any) => {
                                  if (ep.episode_number >= episode.episode_number) return false;
                                  const epAirDate = ep.air_date ? parseTMDBDate(ep.air_date)! : null;
                                  return epAirDate && epAirDate > now;
                                });

                                // Disable if episode is in future OR if a previous episode has a future date and this one has no date
                                const isDisabled = isInFuture || (hasFutureEpisodeBefore && !episode.air_date);

                                return (
                                  <button
                                    key={episode.episode_number}
                                    onClick={async (e) => {
                                      e.preventDefault();
                                      e.stopPropagation();

                                      if (isDisabled && !isEpisodeWatched) return;

                                      if (isEpisodeWatched) {
                                        await markEpisodeUnwatched(item.id, season.season_number, episode.episode_number);
                                      } else {
                                        await markEpisodeWatched(item.id, season.season_number, episode.episode_number);

                                        // Check if season is now complete
                                        const newWatchedCount = (watchlistItem.watchedEpisodes?.[season.season_number]?.length || 0) + 1;
                                        if (newWatchedCount >= season.episodes.length) {
                                          await markSeasonWatched(item.id, season.season_number);
                                        }
                                      }
                                    }}
                                    disabled={isDisabled && !isEpisodeWatched}
                                    className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                                      isEpisodeWatched
                                        ? 'bg-gradient-to-r from-green-950/40 to-green-900/20 ring-1 ring-green-600/30 active:scale-[0.98]'
                                        : isDisabled
                                        ? 'bg-gray-800/20 ring-1 ring-gray-700/20 opacity-50 cursor-not-allowed'
                                        : 'bg-gray-800/30 hover:bg-gray-800/50 ring-1 ring-gray-700/30 active:scale-[0.98]'
                                    }`}
                                  >
                                    {/* Episode Number Badge */}
                                    <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs ${
                                      isEpisodeWatched
                                        ? 'bg-green-600/30 text-green-400 ring-1 ring-green-500/50'
                                        : 'bg-gray-700/50 text-gray-400 ring-1 ring-gray-600/50'
                                    }`}>
                                      {episode.episode_number}
                                    </div>

                                    {/* Episode Details */}
                                    <div className="flex-1 min-w-0 text-left">
                                      <div className={`text-sm font-medium line-clamp-1 ${
                                        isEpisodeWatched ? 'text-white' : 'text-gray-200'
                                      }`}>
                                        {episode.name}
                                      </div>
                                      {episode.air_date && (
                                        <div className="text-xs text-gray-500 mt-0.5">
                                          {format(parseTMDBDate(episode.air_date)!, 'MMM d, yyyy')}
                                        </div>
                                      )}
                                    </div>

                                    {/* Checkmark / Lock Icon */}
                                    <div className="flex-shrink-0">
                                      {isEpisodeWatched ? (
                                        <div className="w-6 h-6 rounded-full bg-green-600 flex items-center justify-center">
                                          <Check className="h-4 w-4 text-white" />
                                        </div>
                                      ) : isDisabled ? (
                                        <div className="w-6 h-6 rounded-full bg-gray-700/50 flex items-center justify-center">
                                          <Lock className="h-3 w-3 text-gray-500" />
                                        </div>
                                      ) : (
                                        <div className="w-6 h-6 rounded-full border-2 border-gray-600" />
                                      )}
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

// Memoize to prevent unnecessary re-renders
export const MediaCard = memo(MediaCardComponent, (prevProps, nextProps) => {
  // Only re-render if the item id, media type, or show add button props change
  return (
    prevProps.item.id === nextProps.item.id &&
    prevProps.mediaType === nextProps.mediaType &&
    prevProps.showAddButton === nextProps.showAddButton &&
    prevProps.size === nextProps.size &&
    prevProps.className === nextProps.className
  );
});